import { useState } from "react";
import { Link } from "react-router";
import {
  CalendarClock,
  CalendarX2,
  Loader2,
  Mail,
  Phone,
  PhoneCall,
  RotateCw,
  TriangleAlert,
  Video,
} from "lucide-react";

import { CancelBookingDialog } from "@/components/doctor/cancel-booking-dialog";
import { BookingStatusBadge } from "@/components/common/status-badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useCancelBooking } from "@/hooks/doctor/use-cancel-booking";
import { ApiError } from "@/lib/api/client";
import { isCallableBooking, type BookingView } from "@/lib/booking-view";
import {
  formatBookingDay,
  formatBookingTimeRange,
  formatCurrency,
  hasBookingStarted,
  initials,
} from "@/lib/format";
import type { Booking } from "@/types/doctor";

const canStartVideoCall = (booking: Booking) =>
  booking.consultationType === "video" && isCallableBooking(booking);

/**
 * Only an open booking can be cancelled. The backend returns 400 for a
 * completed or already-cancelled one, and cancelling a slot that has already
 * come and gone is meaningless, so `missed` is out too.
 */
const canCancel = (booking: Booking) => booking.status === "booked";

/** "12m 30s" / "45s" — the length of the consultation that actually happened. */
function formatCallDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

export function BookingCard({
  booking,
  view,
  now,
}: {
  booking: Booking;
  view: BookingView;
  now: Date;
}) {
  const [confirming, setConfirming] = useState(false);
  const cancel = useCancelBooking();

  const confirmCancel = () => {
    void cancel.mutateAsync(booking.id).then(
      () => setConfirming(false),
      () => {
        /* the hook has already surfaced the failure; keep the dialog open */
      },
    );
  };

  return (
    <li className="card-surface hover-lift p-5">
      <div className="flex flex-wrap items-start gap-4">
        <Avatar className="size-11 border border-border">
          <AvatarFallback className="bg-secondary text-sm font-semibold text-secondary-foreground">
            {initials(booking.patientName)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-semibold">{booking.patientName}</h3>
            <BookingStatusBadge status={view} />
            {booking.consultationType === "video" && (
              <span className="inline-flex items-center gap-1 rounded-full border border-info/25 bg-info/10 px-2 py-0.5 text-xs font-medium text-info">
                <Video className="size-3" /> Video
              </span>
            )}
            {/* The slot is running right now — the one case worth calling out. */}
            {view === "booked" && hasBookingStarted(booking, now) && (
              <span className="inline-flex items-center gap-1 rounded-full border border-success/25 bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                <span className="size-1.5 animate-pulse rounded-full bg-current" /> In progress
              </span>
            )}
          </div>

          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="size-3.5" />
              {formatBookingDay(booking, now)} · {formatBookingTimeRange(booking)}
            </span>
            {booking.patientAge && <span>{booking.patientAge} yrs</span>}
            {booking.patientGender && <span className="capitalize">{booking.patientGender}</span>}
            {/* Server-computed call outcome (backend_update.md §3): evidence the
                consultation happened, and for how long. */}
            {booking.callDurationSeconds != null && booking.callDurationSeconds > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <PhoneCall className="size-3.5" />
                {formatCallDuration(booking.callDurationSeconds)} call
              </span>
            )}
            {/* A patient who joined but whose call was never ended cleanly has no
                duration — say they attended rather than show nothing. */}
            {booking.callDurationSeconds == null && booking.patientJoinedAt && (
              <span className="inline-flex items-center gap-1.5">
                <PhoneCall className="size-3.5" />
                Patient joined
              </span>
            )}
          </p>

          {(booking.patientPhone || booking.patientEmail) && (
            <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {booking.patientPhone && (
                <a
                  href={`tel:${booking.patientPhone}`}
                  className="inline-flex items-center gap-1.5 hover:text-foreground"
                >
                  <Phone className="size-3" /> {booking.patientPhone}
                </a>
              )}
              {booking.patientEmail && (
                <a
                  href={`mailto:${booking.patientEmail}`}
                  className="inline-flex items-center gap-1.5 hover:text-foreground"
                >
                  <Mail className="size-3" /> {booking.patientEmail}
                </a>
              )}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-3">
          <p className="font-display text-lg font-semibold whitespace-nowrap">
            {formatCurrency(booking.fees)}
          </p>
          {(canStartVideoCall(booking) || canCancel(booking)) && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canStartVideoCall(booking) && (
                <Button asChild size="sm" className="gap-2">
                  <Link to={`/bookings/${booking.id}/call`}>
                    <Video className="size-4" aria-hidden /> Start video call
                  </Link>
                </Button>
              )}
              {canCancel(booking) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirming(true)}
                  disabled={cancel.isPending}
                  aria-label={`Cancel appointment with ${booking.patientName}`}
                >
                  {cancel.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <CalendarX2 className="size-4" aria-hidden />
                  )}
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <CancelBookingDialog
        booking={booking}
        open={confirming}
        onOpenChange={setConfirming}
        onConfirm={confirmCancel}
        pending={cancel.isPending}
      />
    </li>
  );
}

export function EmptyBookings({ message }: { message: string }) {
  return (
    <div className="card-surface grid place-items-center px-6 py-14 text-center">
      <span className="grid size-12 place-items-center rounded-2xl bg-muted text-muted-foreground">
        <CalendarClock className="size-5" />
      </span>
      <p className="mt-4 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/**
 * Shown when the bookings request itself failed.
 *
 * This is deliberately not the empty state. "You have no appointments" and "we
 * could not load your appointments" are opposite facts, and rendering the first
 * for the second is how a backend outage comes to look like a scheduling bug —
 * a doctor with a full day reads "nothing coming up" and trusts it.
 */
export function BookingsLoadError({
  error,
  onRetry,
  retrying = false,
}: {
  error: unknown;
  onRetry: () => void;
  retrying?: boolean | undefined;
}) {
  const status = error instanceof ApiError ? error.status : 0;
  const serverFault = status >= 500 || status === 0;

  // A 4xx is about this doctor's session and its message is written for them
  // ("Your session expired"). A 5xx message is the backend's internal wording
  // — "Error fetching bookings" — which tells a doctor nothing, so it is
  // demoted to a technical detail under an explanation in our own voice.
  const body = serverFault
    ? "The HealNova server didn't return your schedule. Nothing has been lost — your appointments are still booked, and they'll reappear here once the service recovers."
    : error instanceof ApiError
      ? error.message
      : "Something went wrong while loading your appointments.";
  const detail = serverFault && error instanceof ApiError ? error.message : null;

  return (
    <div className="grid place-items-center rounded-2xl border border-destructive/25 bg-destructive/[0.04] px-6 py-14 text-center">
      <span className="grid size-12 place-items-center rounded-2xl bg-destructive/10 text-destructive">
        <TriangleAlert className="size-5" />
      </span>
      <h3 className="mt-4 font-display text-lg font-semibold">
        We couldn't load your appointments
      </h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground text-pretty">
        {body}
      </p>
      {detail && (
        <p className="mt-3 font-mono text-xs text-muted-foreground/70">
          {status} · {detail}
        </p>
      )}
      <Button variant="outline" className="mt-6" onClick={onRetry} disabled={retrying}>
        {retrying ? <Loader2 className="size-4 animate-spin" /> : <RotateCw className="size-4" />}
        Try again
      </Button>
    </div>
  );
}
